import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, Linkedin, Lock, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ERROR_MESSAGES: Record<string, string> = {
  not_registered: "No account found. Please register first to create your Udyoga Pramoda account.",
  oauth_failed: "LinkedIn sign-in failed. Please try again.",
  oauth_denied: "LinkedIn sign-in was cancelled or denied. Please try again.",
  oauth_not_configured: "LinkedIn sign-in is not available right now. Please try again later or contact support.",
  oauth_state_missing: "Sign-in session expired. Please try again.",
  linkedin_profile_incomplete: "Could not retrieve your LinkedIn profile. Please try again.",
  linkedin_mismatch: "This LinkedIn account does not match the one used during registration. Please use the same LinkedIn account.",
  account_deactivated: "Your account has been deactivated. Please contact support.",
  pending_approval:
    "Your account is pending admin approval. Reviews typically take 24–48 hours. You will receive an email once approved, then you can sign in.",
  account_rejected: "Your registration was not approved. Please contact support if you have questions.",
  email_not_verified: "Please verify your email with the OTP sent during registration before signing in.",
};

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading, login } = useAuth();
  const [error, setError] = useState("");
  // Set when the server tells us this account belongs elsewhere, so the page
  // can offer the right way in rather than just refusing.
  const [errorCode, setErrorCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  useEffect(() => {
    const urlError = searchParams.get("error");
    if (urlError) {
      setError(ERROR_MESSAGES[urlError] || "Sign-in failed. Please try again.");
    }
  }, [searchParams]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;

    if (user.role === "admin") {
      navigate("/admin/dashboard", { replace: true });
      return;
    }

    if (!user.isEmailVerified) {
      navigate("/verify-otp", { replace: true, state: { email: user.email } });
      return;
    }

    if (!user.isApproved) {
      navigate("/awaiting-approval", { replace: true });
      return;
    }

    navigate("/", { replace: true });
  }, [isAuthenticated, isLoading, navigate, user]);

  const handleLinkedInLogin = () => {
    window.location.href = "/api/auth/linkedin?mode=login";
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) { setError(""); setErrorCode(""); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email.trim() || !formData.password) {
      setError("Enter your email and password to sign in.");
      return;
    }

    setLoading(true);
    setError("");
    setErrorCode("");

    try {
      await login({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });
      // The redirect effect above routes the member once the session loads.
    } catch (err: any) {
      setError(err?.response?.data?.message || "Sign-in failed. Please try again.");
      setErrorCode(err?.response?.data?.code || "");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-xl sm:text-2xl font-bold text-secondary mb-2">
            Welcome Back
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            Sign in to your Udyoga Pramoda account
          </p>
        </div>

        <Card className="border-border shadow-lg">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-semibold text-center text-secondary">
              Sign In
            </CardTitle>
            <CardDescription className="text-center text-gray-600">
              Use your email and password, or continue with LinkedIn
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
                {searchParams.get("error") === "not_registered" && (
                  <div className="mt-2">
                    <Link to="/register" className="text-primary hover:underline font-medium">
                      Create an account
                    </Link>
                  </div>
                )}
                {errorCode === "USE_ADMIN_LOGIN" && (
                  <div className="mt-2">
                    <Link to="/admin/login" className="text-primary hover:underline font-medium">
                      Go to administrator sign-in
                    </Link>
                  </div>
                )}
                {errorCode === "USE_LINKEDIN_LOGIN" && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={handleLinkedInLogin}
                      className="font-medium text-primary hover:underline"
                    >
                      Continue with LinkedIn instead
                    </button>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-secondary">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="pl-10 h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-secondary">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="pl-10 pr-10 h-11 border-border focus:border-primary focus:ring-primary/20 transition-colors"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary-dark font-medium"
                >
                  Forgot password?
                </Link>
              </div>

              <Button type="submit" disabled={loading} className="w-full h-11">
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-wider">
                <span className="bg-white px-2 text-gray-500">or</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleLinkedInLogin}
              className="w-full h-11 bg-[#0A66C2] hover:bg-[#004182] text-white"
            >
              <Linkedin className="h-4 w-4 mr-2" />
              Continue with LinkedIn
            </Button>

            <div className="space-y-2 text-center text-sm text-gray-600">
              <div>
                Don&apos;t have an account?{" "}
                <Link to="/register" className="text-primary hover:text-primary-dark font-medium">
                  Register here
                </Link>
              </div>
              <div>
                <Link to="/admin/login" className="text-gray-500 hover:text-gray-700 hover:underline">
                  Administrator sign-in
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
